import React from 'react'
import styled from 'styled-components'
import moment from 'moment-timezone'
import {injectIntl} from 'react-intl'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Menu from '@mui/material/Menu'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import DateTimeFormat from 'components/DateTimeFormat'
import {getTypeReference, listFilter} from 'utils/utils'
import {fieldtype} from 'utils/enums/fieldtype'

const EXTRA_ROWS = 1
const ROW_HEIGHT = 36
const ROWS_COUNT = 8
const TEXT_FIELD_HEIGHT = 50
const NO_VALUE = -1

const Label = styled(FormControlLabel)`
	width: 100%;
	margin-right: 0;
	margin-bottom: 20px;
`

const Search = styled(TextField)`
	width: auto;
	padding: 10px;
	position: sticky;
	top: 0;
	background: #FFFFFF;
	z-index: 1;
	display: flex;
	height: ${TEXT_FIELD_HEIGHT}px;
	box-sizing: border-box;
`

const SelectControl = styled(FormControl)`
	width: 100%;
	margin-bottom: 20px;
`

class DateFieldConfig extends React.Component {
    constructor(props) {
        super(props)

        this.timezones = []
        this.fields = getTypeReference(props.entity, fieldtype.datetime).filter(item => item.fieldId !== props.targetCol)

        this.state = {
            timezone: props.config.timezone ? true : false,
            timezoneValue: props.config.timezone,
            dateType: props.config.type ? props.config.type : 'datetime',
            mindate: props.config.mindate ? props.config.mindate.join(',') : NO_VALUE,
            maxdate: props.config.maxdate ? props.config.maxdate.join(',') : NO_VALUE,
            range: props.config.mindate || props.config.maxdate ? true : false,
            format: props.config.format ? props.config.format : 'DD MMMM YYYY HH:mm',
            anchorEl: null,
            startRow: 0,
            endRow: EXTRA_ROWS,
            search: ''
        }
    }

    handleChange = (event) => {
        this.setState({
            [event.target.name]: event.target.value
        })
    }

    handleChangeFormat = (format) => {
        this.setState({ ...format })
    }

    changeType = () => {
        this.props.changeTypeToDate({
            format: this.state.format,
            type: this.state.dateType,
            timezone: this.state.dateType === 'datetime' && this.state.timezone ?
				this.state.timezoneValue : '',
            mindate: this.state.range && this.state.mindate !== -1 ?
                this.fields.find(item => item.id === this.state.mindate).pathId : null,
            maxdate: this.state.range && this.state.maxdate !== -1 ?
                this.fields.find(item => item.id === this.state.maxdate).pathId : null
        })

        this.props.onClose()
        this.props.handleCloseMenu()
    }

    handleToggle = (e) => {
		const checked = e.target.checked

        if (e.target.name === 'timezone') {
            this.setState(state => ({
                timezone: checked,
                timezoneValue: state.timezoneValue ? state.timezoneValue : moment.tz.guess()
            }))
        } else {
            this.setState({
                [e.target.name]: checked
            })
        }
    }

    handleSearch = (e) => {
        const list = moment.tz.names()
        this.timezones = listFilter(list, e.currentTarget.value)

        this.setState({
            search: e.currentTarget.value,
            startRow: 0,
            endRow: ROWS_COUNT + EXTRA_ROWS,
        })
    }

    getVisibleRows = (e) => {
        let container

        if (!container) {
            container = document.getElementById('container')
        }

        const scrollTop = container ? container.scrollTop : 0
        const currentIndex = Math.trunc(scrollTop / ROW_HEIGHT)
        const startRow = Math.max(currentIndex - EXTRA_ROWS, 0)
        const endRow = Math.min(currentIndex + ROWS_COUNT + EXTRA_ROWS, this.timezones.length)
        if (startRow !== this.state.startRow || endRow !== this.state.endRow) {
            this.setState({
                startRow,
                endRow
            })
        }
    }

    handleOpenMenu = (e) => {
        this.timezones = moment.tz.names()
        this.setState({ anchorEl: e.currentTarget }, () => this.getVisibleRows())
    }

    handleCloseMenu = () => {
        this.setState({ anchorEl: null, search: '' })
    }

    handleSelect = (value) => {
        this.setState({ timezoneValue: value })
        this.handleCloseMenu()
    }

    getListItem = (index, item) => {
        return <MenuItem
            key={index}
            value={item}
            selected={item === this.state.timezoneValue}
            onClick={() => this.handleSelect(item)}
        >
            {item}
        </MenuItem>
    }

    getList = () => {
        const list = []

        for (let i = this.state.startRow; i < this.state.endRow; i++) {
            if (!this.timezones[i]) break
            list.push(this.getListItem(i, this.timezones[i]))
        }

        return list
    }

    render() {
        const {intl} = this.props
        const list = !!this.state.anchorEl && this.getList()

        return (
            <React.Fragment>
                <DialogTitle>
                    {intl.formatMessage({id: "config.date_field_title"})}
                </DialogTitle>
                <DialogContent>
                    <DateTimeFormat
                        type={this.state.dateType}
                        format={this.state.format}
                        handleChange={this.handleChangeFormat}
                    />
                    {this.state.dateType === 'datetime' &&
						<Label
							control={
								<Checkbox
									sx={{
										padding: '0 9px'
									}}
									checked={this.state.timezone}
									onChange={this.handleToggle}
									name='timezone'
								/>
							}
							label={intl.formatMessage({id: "config.date_field_timezone"})}
						/>
					}
                    {this.state.dateType === 'datetime' && this.state.timezone &&
                        <TextField variant="standard"
                            label={intl.formatMessage({id: "config.date_format"})}
                            onClick={this.handleOpenMenu}
                            InputProps={{
                                readOnly: true
                            }}
                            sx={{
								width: '100%',
        						marginBottom: '20px'
							}}
                            value={this.state.timezoneValue}
                        />
                    }
                    <Label
                        control={
                            <Checkbox
								sx={{
									padding: '0 9px'
								}}
                                checked={this.state.range}
                                onChange={this.handleToggle}
                                name='range'
                            />
                        }
                        label={intl.formatMessage({id: "config.date_set_range"})}
                    />
                    {this.state.range &&
                        <React.Fragment>
                            <SelectControl variant="standard">
                                <InputLabel shrink={true}>{intl.formatMessage({id: "config.date_min_value"})}</InputLabel>
                                <Select
                                    value={this.state.mindate}
                                    onChange={this.handleChange}
                                    inputProps={{
                                        name: 'mindate'
                                    }}
                                >
                                    <MenuItem value={NO_VALUE} selected={this.state.mindate === NO_VALUE}>
                                        {intl.formatMessage({id: "config.date_any"})}
                                    </MenuItem>
                                    {this.fields.map((item, index) =>
                                        <MenuItem selected={this.state.mindate === item.id} key={index} value={item.id}>
                                            {item.pathName}
                                        </MenuItem>
                                    )}
                                </Select>
                            </SelectControl>
                            <SelectControl variant="standard">
                                <InputLabel shrink={true}>{intl.formatMessage({id: "config.date_max_value"})}</InputLabel>
                                <Select
                                    value={this.state.maxdate}
                                    onChange={this.handleChange}
                                    inputProps={{
                                        name: 'maxdate'
                                    }}
                                >
                                    <MenuItem value={NO_VALUE} selected={this.state.maxdate === NO_VALUE}>
                                        {intl.formatMessage({id: "config.date_any"})}
                                    </MenuItem>
                                    {this.fields.map((item, index) =>
                                        <MenuItem selected={this.state.maxdate === item.id} key={index} value={item.id}>
                                            {item.pathName}
                                        </MenuItem>
                                    )}
                                </Select>
                            </SelectControl>
                        </React.Fragment>
                    }
                    <Menu
                        open={!!this.state.anchorEl}
                        onClose={this.handleCloseMenu}
                        anchorEl={this.state.anchorEl}
                        variant='menu'
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            onScroll: this.getVisibleRows,
                            id: 'container',
                            style: {
                                maxHeight: ROWS_COUNT * ROW_HEIGHT
                            }
                        }}

                        
                        MenuListProps={{
                            style: {
                                height: this.timezones.length * ROW_HEIGHT + TEXT_FIELD_HEIGHT,
                                width: '300px',
                                paddingTop: 0
                            }
                        }}
                    >
                        <MenuItem style={{height: this.state.startRow * ROW_HEIGHT, padding: 0}} />
                        <Search variant="standard"
                            onChange={this.handleSearch}
                            autoFocus
                            value={this.state.search}
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                        {list}
                    </Menu>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.onClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    <Button onClick={this.changeType} color="primary" variant="contained">
                        {intl.formatMessage({id: "app.confirm_save"})}
                    </Button>
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(DateFieldConfig)
